import {
  LEGAL_OBLIGATION_CONDITION_AXIS_VALUE_KIND,
  normalizeAnnexNumber,
  normalizeAnnexSubNumber,
  type AdminLegalObligationConditionView,
  type AdminLegalObligationIntervalVariantView,
  type CreateAdminLegalObligationConditionBody,
  type CreateAdminLegalObligationIntervalVariantBody,
  type LegalObligationConditionAxisValue,
  type LegalObligationConditionSourceKindValue,
  type ObligationIntervalAnchor,
  type ObligationIntervalUnit,
  type ObligationIntervalVariantSelector,
  type ObligationIntervalVariantTarget,
  type UpdateAdminLegalObligationConditionBody,
  type UpdateAdminLegalObligationIntervalVariantBody,
} from '@safetyhub/shared';

/**
 * 카탈로그 **조건·변형 줄** 편집기의 순수 판정 — 폼 상태 · 저장 본문 · 저장 전 막을 사유(W3 2026-11-04).
 *
 * API 클라이언트(`legalObligationCatalog.ts`)와 가른 이유는 형제 `legalObligationCatalogFields.ts`와 같다(화면 spec의 `vi.mock`).
 *
 * ## 판정의 정본은 서버다
 * 서버 `legal-obligation-catalog-shape.ts`가 축 형상 · 근거 형상 · 근거 실재 · 변형 형상을 «저장 뒤 형상»으로 판정하고 400 + 사유 코드를 준다.
 * 여기서 막는 것은 **폼만 보고 닫히는 것**(수 형식 · 하한 ≤ 상한 · 근거 종류가 별표인데 별표를 안 골랐다)뿐이다 — 왕복을 줄이는 것이지
 * 판정을 두 벌로 두는 것이 아니다. 단위는 shared `LEGAL_OBLIGATION_CONDITION_AXIS_VALUE_KIND`(서버 판정과 같은 표)에서 파생하고 사람이 고르지 않는다.
 *
 * ## 저장은 «달라진 칸만»
 * 조건 PATCH는 규칙 값이 바뀌면 개정, 근거가 바뀌면 별 기록이다(서버 docblock) — 안 바꾼 칸을 다시 보내면 정규화 차이로 «바꾸지 않은 축»이
 * 기록될 수 있어 형제 `buildUpdateBody`와 같은 규약을 쓴다.
 */

// ── 조건 줄 ────────────────────────────────────────────────────────────────

export interface ConditionFormState {
  axis: LegalObligationConditionAxisValue;
  /** 수 문자열 — 빈 값이면 NULL(수치 축만) */
  minValue: string;
  maxValue: string;
  minInclusive: boolean;
  maxInclusive: boolean;
  /** 열거 축의 값 집합 — 쉼표로 가른다(KSIC 코드 · 기술지도 구분 · 호 번호 등 축의 값 키 그대로) */
  valueKeysText: string;
  variesByIndustry: boolean;
  sourceNote: string;
  sourceKind: LegalObligationConditionSourceKindValue;
  sourceLawId: string;
  sourceAdminRuleId: string;
  sourceAnnexNumber: string;
  sourceAnnexSubNumber: string;
  /** 개정 사유 — 규칙 값이 바뀌어 개정이 일어날 때 이력 행에 적힌다 */
  changeReason: string;
}

/** 축의 단위 — 수치 축이면 그 단위, 열거 축이면 null(서버 `CONDITION_UNIT_MISMATCH`·`CONDITION_ENUM_HAS_UNIT`과 같은 표) */
export function conditionAxisUnit(axis: LegalObligationConditionAxisValue): string | null {
  const shape = LEGAL_OBLIGATION_CONDITION_AXIS_VALUE_KIND[axis];
  return shape.kind === 'NUMERIC' ? shape.unit : null;
}

export function isNumericAxis(axis: LegalObligationConditionAxisValue): boolean {
  return LEGAL_OBLIGATION_CONDITION_AXIS_VALUE_KIND[axis].kind === 'NUMERIC';
}

export function emptyConditionForm(axis: LegalObligationConditionAxisValue): ConditionFormState {
  return {
    axis,
    minValue: '',
    maxValue: '',
    minInclusive: true,
    maxInclusive: false,
    valueKeysText: '',
    variesByIndustry: false,
    sourceNote: '',
    sourceKind: 'UNSPECIFIED',
    sourceLawId: '',
    sourceAdminRuleId: '',
    sourceAnnexNumber: '',
    sourceAnnexSubNumber: '',
    changeReason: '',
  };
}

export function conditionToForm(c: AdminLegalObligationConditionView): ConditionFormState {
  return {
    axis: c.axis,
    minValue: c.minValue ?? '',
    maxValue: c.maxValue ?? '',
    minInclusive: c.minInclusive,
    maxInclusive: c.maxInclusive,
    valueKeysText: c.valueKeys.join(', '),
    variesByIndustry: c.variesByIndustry,
    sourceNote: c.sourceNote ?? '',
    sourceKind: c.sourceKind,
    sourceLawId: c.sourceLawId ?? '',
    sourceAdminRuleId: c.sourceAdminRuleId ?? '',
    sourceAnnexNumber: c.sourceAnnexNumber ?? '',
    sourceAnnexSubNumber: c.sourceAnnexSubNumber,
    changeReason: '',
  };
}

function nullableText(value: string): string | null {
  const t = value.trim();
  return t === '' ? null : t;
}

/** 쉼표로 가른 값 집합 — 앞뒤 공백·빈 조각을 걷는다(중복은 걷지 않는다 — 서버가 `CONDITION_VALUE_KEYS_DUPLICATE`로 알린다) */
export function parseValueKeys(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** 수 문자열 비교 — 「50」과 「50.00」은 같은 값이다(서버가 Decimal로 저장해 응답이 다른 표기로 올 수 있다) */
function sameDecimal(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return a === b;
  return Number(a) === Number(b);
}

/** 별표 근거의 키 — 종류에 해당하는 쪽만 남기고 나머지는 비운다(서버 `SOURCE_KEY_NOT_FOR_KIND`를 폼에서 만들지 않게) */
function sourceOf(form: ConditionFormState) {
  const annex = form.sourceKind === 'LAW_ANNEX' || form.sourceKind === 'ADMIN_RULE_ANNEX';
  const annexNumber = nullableText(form.sourceAnnexNumber);
  return {
    sourceKind: form.sourceKind,
    sourceLawId: form.sourceKind === 'LAW_ANNEX' ? nullableText(form.sourceLawId) : null,
    sourceAdminRuleId: form.sourceKind === 'ADMIN_RULE_ANNEX' ? nullableText(form.sourceAdminRuleId) : null,
    sourceAnnexNumber: annex && annexNumber !== null ? normalizeAnnexNumber(annexNumber) : null,
    sourceAnnexSubNumber: annex ? normalizeAnnexSubNumber(form.sourceAnnexSubNumber.trim()) : '',
  };
}

/** 규칙 값 — 축의 형상에 맞는 칸만 채운다(수치 축 = 수치·단위 · 열거 축 = 값 집합) */
function ruleOf(form: ConditionFormState) {
  const numeric = isNumericAxis(form.axis);
  return {
    minValue: numeric ? nullableText(form.minValue) : null,
    maxValue: numeric ? nullableText(form.maxValue) : null,
    minInclusive: form.minInclusive,
    maxInclusive: form.maxInclusive,
    unit: conditionAxisUnit(form.axis),
    valueKeys: numeric ? [] : parseValueKeys(form.valueKeysText),
    variesByIndustry: form.variesByIndustry,
    sourceNote: nullableText(form.sourceNote),
  };
}

const DECIMAL_RE = /^\d+(\.\d{1,2})?$/;

/** 저장을 막는 사유 — 폼만 보고 닫히는 것만(파일 docblock). 없으면 `undefined` */
export function conditionFormBlockedReason(form: ConditionFormState): string | undefined {
  if (isNumericAxis(form.axis)) {
    for (const [label, v] of [['하한', form.minValue], ['상한', form.maxValue]] as const) {
      if (v.trim() !== '' && !DECIMAL_RE.test(v.trim())) return `${label}은 0 이상의 수(소수 둘째 자리까지)여야 합니다.`;
    }
    if (form.minValue.trim() !== '' && form.maxValue.trim() !== '' && Number(form.minValue) > Number(form.maxValue)) {
      return '하한이 상한보다 큽니다.';
    }
  }
  if (form.sourceKind === 'LAW_ANNEX' && (form.sourceLawId.trim() === '' || form.sourceAnnexNumber.trim() === '')) {
    return '근거가 법령 별표면 별표를 골라 주세요.';
  }
  if (form.sourceKind === 'ADMIN_RULE_ANNEX' && (form.sourceAdminRuleId.trim() === '' || form.sourceAnnexNumber.trim() === '')) {
    return '근거가 고시 별표면 별표를 골라 주세요.';
  }
  return undefined;
}

export function buildConditionCreateBody(form: ConditionFormState): CreateAdminLegalObligationConditionBody {
  const body: CreateAdminLegalObligationConditionBody = { axis: form.axis, ...ruleOf(form), ...sourceOf(form) };
  const reason = nullableText(form.changeReason);
  if (reason !== null) body.changeReason = reason;
  return body;
}

/** 수정 본문 — 달라진 칸만. 아무것도 안 바뀌었으면 null(사유만으로는 개정이 아니다) */
export function buildConditionUpdateBody(
  current: AdminLegalObligationConditionView,
  form: ConditionFormState,
): UpdateAdminLegalObligationConditionBody | null {
  const rule = ruleOf(form);
  const source = sourceOf(form);
  const body: UpdateAdminLegalObligationConditionBody = {};
  if (!sameDecimal(rule.minValue, current.minValue)) body.minValue = rule.minValue;
  if (!sameDecimal(rule.maxValue, current.maxValue)) body.maxValue = rule.maxValue;
  if (rule.minInclusive !== current.minInclusive) body.minInclusive = rule.minInclusive;
  if (rule.maxInclusive !== current.maxInclusive) body.maxInclusive = rule.maxInclusive;
  if (rule.unit !== current.unit) body.unit = rule.unit;
  if (!sameList(rule.valueKeys, current.valueKeys)) body.valueKeys = rule.valueKeys;
  if (rule.variesByIndustry !== current.variesByIndustry) body.variesByIndustry = rule.variesByIndustry;
  if (rule.sourceNote !== nullableText(current.sourceNote ?? '')) body.sourceNote = rule.sourceNote;
  if (source.sourceKind !== current.sourceKind) body.sourceKind = source.sourceKind;
  if (source.sourceLawId !== current.sourceLawId) body.sourceLawId = source.sourceLawId;
  if (source.sourceAdminRuleId !== current.sourceAdminRuleId) body.sourceAdminRuleId = source.sourceAdminRuleId;
  if (source.sourceAnnexNumber !== current.sourceAnnexNumber) body.sourceAnnexNumber = source.sourceAnnexNumber;
  if (source.sourceAnnexSubNumber !== current.sourceAnnexSubNumber) body.sourceAnnexSubNumber = source.sourceAnnexSubNumber;
  if (Object.keys(body).length === 0) return null;
  const reason = nullableText(form.changeReason);
  if (reason !== null) body.changeReason = reason;
  return body;
}

// ── 변형 줄 ────────────────────────────────────────────────────────────────

export interface VariantFormState {
  target: ObligationIntervalVariantTarget;
  selector: ObligationIntervalVariantSelector;
  /** `SITE_FACT`만 — 그 밖은 `''` */
  axis: LegalObligationConditionAxisValue | '';
  valueKeysText: string;
  label: string;
  intervalUnit: ObligationIntervalUnit;
  /** 정수 문자열 */
  intervalCount: string;
  /** `PERIOD`만 — `''` = 규칙의 기산 그대로 */
  intervalAnchor: ObligationIntervalAnchor | '';
  oneCycleOnly: boolean;
  sourceNote: string;
  changeReason: string;
}

export function emptyVariantForm(target: ObligationIntervalVariantTarget): VariantFormState {
  return {
    target,
    selector: target === 'EVENT_DEADLINE' ? 'EVENT_CHOICE' : 'SITE_FACT',
    axis: target === 'EVENT_DEADLINE' ? '' : 'INDUSTRY',
    valueKeysText: '',
    label: '',
    intervalUnit: target === 'EVENT_DEADLINE' ? 'DAY' : 'YEAR',
    intervalCount: '',
    intervalAnchor: '',
    oneCycleOnly: false,
    sourceNote: '',
    changeReason: '',
  };
}

export function variantToForm(v: AdminLegalObligationIntervalVariantView): VariantFormState {
  return {
    target: v.target,
    selector: v.selector,
    axis: v.axis ?? '',
    valueKeysText: v.valueKeys.join(', '),
    label: v.label ?? '',
    intervalUnit: v.interval.unit,
    intervalCount: String(v.interval.count),
    intervalAnchor: v.anchor ?? '',
    oneCycleOnly: v.oneCycleOnly,
    sourceNote: v.sourceNote ?? '',
    changeReason: '',
  };
}

/** 선택자·대상에 맞지 않는 칸은 비워 싣는다(서버 `VARIANT_AXIS_ONLY_SITE_FACT`·`VARIANT_ANCHOR_ONLY_PERIOD` 등을 폼이 만들지 않게) */
function variantValuesOf(form: VariantFormState) {
  return {
    target: form.target,
    selector: form.selector,
    axis: form.selector === 'SITE_FACT' && form.axis !== '' ? form.axis : null,
    valueKeys: form.selector === 'EVENT_CHOICE' ? [] : parseValueKeys(form.valueKeysText),
    label: nullableText(form.label),
    intervalUnit: form.intervalUnit,
    intervalCount: Number(form.intervalCount.trim()),
    intervalAnchor: form.target === 'PERIOD' && form.intervalAnchor !== '' ? form.intervalAnchor : null,
    oneCycleOnly: form.selector === 'RESULT_FACT' ? form.oneCycleOnly : false,
    sourceNote: nullableText(form.sourceNote),
  };
}

export function variantFormBlockedReason(form: VariantFormState): string | undefined {
  const n = form.intervalCount.trim();
  if (n === '' || !Number.isInteger(Number(n))) return '간격은 정수여야 합니다.';
  if (form.selector === 'SITE_FACT' && form.axis === '') return '현장 사실 변형은 축을 골라야 합니다.';
  if (form.selector !== 'EVENT_CHOICE' && parseValueKeys(form.valueKeysText).length === 0) return '현장 사실·결과 사실·대상자 사실 변형은 값 집합이 필요합니다.';
  if (form.selector === 'EVENT_CHOICE' && form.label.trim() === '') return '사건 선택 변형은 선택지 문구가 필요합니다.';
  return undefined;
}

export function buildVariantCreateBody(form: VariantFormState): CreateAdminLegalObligationIntervalVariantBody {
  const body: CreateAdminLegalObligationIntervalVariantBody = variantValuesOf(form);
  const reason = nullableText(form.changeReason);
  if (reason !== null) body.changeReason = reason;
  return body;
}

export function buildVariantUpdateBody(
  current: AdminLegalObligationIntervalVariantView,
  form: VariantFormState,
): UpdateAdminLegalObligationIntervalVariantBody | null {
  const next = variantValuesOf(form);
  const body: UpdateAdminLegalObligationIntervalVariantBody = {};
  if (next.target !== current.target) body.target = next.target;
  if (next.selector !== current.selector) body.selector = next.selector;
  if (next.axis !== current.axis) body.axis = next.axis;
  if (!sameList(next.valueKeys, current.valueKeys)) body.valueKeys = next.valueKeys;
  if (next.label !== nullableText(current.label ?? '')) body.label = next.label;
  if (next.intervalUnit !== current.interval.unit) body.intervalUnit = next.intervalUnit;
  if (next.intervalCount !== current.interval.count) body.intervalCount = next.intervalCount;
  if (next.intervalAnchor !== current.anchor) body.intervalAnchor = next.intervalAnchor;
  if (next.oneCycleOnly !== current.oneCycleOnly) body.oneCycleOnly = next.oneCycleOnly;
  if (next.sourceNote !== nullableText(current.sourceNote ?? '')) body.sourceNote = next.sourceNote;
  if (Object.keys(body).length === 0) return null;
  const reason = nullableText(form.changeReason);
  if (reason !== null) body.changeReason = reason;
  return body;
}
